import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import {
  getFacilityBookings,
  getSocietyResidents,
} from '../../../api/statistics'
import { ExportCard } from './ExportsCard'
import { ISocietiesModel } from '../../../interfaces/models/societies.interfaces'
import { facilityQueries } from '../../../api/facilities'
import { i18nSocietyType } from '../../../i18n'

interface ExportSectionTable {
  title: string
  cards: ExportCard[]
}

interface ExportSectionTableProps extends ExportSectionTable {
  className?: string
}

export const ExportSectionTable = ({
  title,
  cards,
  className,
}: ExportSectionTableProps): JSX.Element => {
  return (
    <div className={`${className ?? ''}`}>
      <p className="my-4" style={theme.textVariants.lead}>
        {title}
      </p>
      <div>
        {cards.map((_card, index) => {
          return (
            <ExportCard
              key={_card.title}
              {..._card}
              className={`${index < cards.length - 1 && 'mb-3'}`}
            />
          )
        })}
      </div>
    </div>
  )
}

interface ExportsSectionProps {
  society: ISocietiesModel
}

export const ExportsSection = ({
  society,
}: ExportsSectionProps): React.ReactNode => {
  const { translate } = useAppTranslation()

  const { data: facilities } = useQuery(
    facilityQueries.getFacilitiesPerSociety(society._id)
  )

  const onResidentListDownClick = async (): Promise<string> => {
    return getSocietyResidents(society._id)
  }

  const sections: ExportSectionTable[] = [
    {
      title: translate(
        'societyStatisticsExportsView.exports.residentList.headerTitle',
        {
          context: i18nSocietyType(society.societyTypeDisplayName),
        }
      ),
      cards: [
        {
          title: translate(
            'societyStatisticsExportsView.exports.residentList.residentListDownload',
            { context: i18nSocietyType(society.societyTypeDisplayName) }
          ),
          getExportLink: onResidentListDownClick,
        },
      ],
    },
    ...(facilities && facilities.length > 0
      ? [
          {
            title: translate(
              'societyStatisticsExportsView.exports.bookingsList.headerTitle'
            ),
            cards: facilities.map((facility) => ({
              title: facility.name,
              getExportLink: async () =>
                getFacilityBookings(society._id, facility._id),
            })),
          },
        ]
      : []),
  ]

  return (
    <div>
      {sections.map((_section, index) => (
        <ExportSectionTable
          key={_section.title}
          title={_section.title}
          cards={_section.cards}
          className={`${index < sections.length - 1 && 'mb-6 md:mb-12'}`}
        />
      ))}
    </div>
  )
}
